import * as React from 'react';
import { useEffect, useState } from 'react';

/** Material UI Packages. */
import { Box, Paper, Tab, Tabs } from '@mui/material';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setTradePartner } from 'shared/store/slices/tradePartnerSlice';
import { setRoute } from 'shared/store/slices/routeSlice';
import { setNewRoute } from 'shared/store/slices/newRouteSlice';
import { setRateTypes } from 'shared/store/slices/rateTypeSlice';
import { setStatusMessages } from 'shared/store/slices/statusMessageSlice';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import SocketAlert from 'shared/ui/SocketAlert';
import TabPanel from 'shared/ui/TabPanel';
import { readSocketData } from 'shared/lib/fetchSocket';

import TradePartner from './components/TradePartner/TradePartner';
import TransportRoute from './components/TransportRoute/TransportRoute';
import RateType from './components/RateType/RateType';
import StatusMessage from './components/StatusMessage/StatusMessage';

export default function DataEntry() {
    /** Dispatch a function to update an edited trade partner. */
    const dispatch = useDispatch();

    /** State for showing the connection status of the WebSocket. */
    const socketConnected = useSelector((state) => state.socketStatus.socketStatus);
    /** Load route state that includes page info, to pass the default setting of paginationModel. */
    const routeData = useSelector((state) => state.routes);

    /** State for indicating whether any subsequent API requests are in progress. */
    const [loading, setLoading] = useState(true);
    /** State for the selected value of tabs.
     * 0: Trade Partner
     * 1: Ocean Route
     * 2: Air Route
     * 3: Rail Route (POD -> F.DEST lead time, drives Ocean Import F.ETA)
     * 4: Rate Type
     * 5: Status Message
     */
    const [tabValue, setTabValue] = useState(0);

    /** Named website title. */
    useEffect(() => {
        document.title = 'Data Entry | Operation System';
    }, []);

    /** Custom hook to call the initial GET API for each tab and load metadata asychronously. */
    useEffect(() => {
        if (!socketConnected) return;

        const tableName =
            tabValue === 0
                ? 'il_data_entry'
                : tabValue === 4
                ? 'il_rate_type'
                : tabValue === 5
                ? 'il_status_message'
                : 'il_route';
        const sendValue =
            tabValue === 0 || tabValue === 4 || tabValue === 5 ? null : JSON.stringify(routeData.paginationModel);
        readSocketData(
            dispatch,
            tabValue,
            'row',
            tableName,
            null,
            sendValue,
            (receivedData) => {
                if (tabValue === 0) {
                    dispatch(setTradePartner(receivedData));
                } else if (tabValue === 1 || tabValue === 2 || tabValue === 3) {
                    dispatch(setRoute(receivedData));
                    dispatch(setNewRoute(receivedData.il_new_route));
                } else if (tabValue === 4) {
                    dispatch(setRateTypes(receivedData));
                } else if (tabValue === 5) {
                    dispatch(setStatusMessages(receivedData));
                }
            },
            () => {},
            () => {
                setLoading(false);
            }
        );
    }, [socketConnected, tabValue]);

    return (
        <>
            <SocketAlert />
            <Paper sx={{ ...customized_style.itemPaper, p: 0 }}>
                <Tabs
                    value={tabValue}
                    onChange={(event, newValue) => setTabValue(newValue)}
                    variant="fullWidth"
                    aria-label="data entry tabs"
                    sx={{
                        borderBottom: 1,
                        borderColor: '#d2d2d4',
                    }}
                >
                    <Tab label="TRADE PARTNER" />
                    <Tab label="OCEAN ROUTE" />
                    <Tab label="AIR ROUTE" />
                    <Tab label="RAIL ROUTE" />
                    <Tab label="RATE TYPE" />
                    <Tab label="STATUS MESSAGE" />
                </Tabs>
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ p: 1 }}>
                        <TradePartner loading={loading} />
                    </Box>
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ p: 1 }}>
                        <TransportRoute loading={loading} tabValue={tabValue} />
                    </Box>
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                    <Box sx={{ p: 1 }}>
                        <TransportRoute loading={loading} tabValue={tabValue} />
                    </Box>
                </TabPanel>
                <TabPanel value={tabValue} index={3}>
                    <Box sx={{ p: 1 }}>
                        <TransportRoute loading={loading} tabValue={tabValue} />
                    </Box>
                </TabPanel>
                <TabPanel value={tabValue} index={4}>
                    <Box sx={{ p: 1 }}>
                        <RateType loading={loading} tabValue={tabValue} />
                    </Box>
                </TabPanel>
                <TabPanel value={tabValue} index={5}>
                    <Box sx={{ p: 1 }}>
                        <StatusMessage loading={loading} tabValue={tabValue} />
                    </Box>
                </TabPanel>
            </Paper>
        </>
    );
}
